import { useState } from "react";
import { changePassword } from "../utils/auth";

// Everyone can change their own password — staff and admins alike. The server
// drops every other session on success, so a change also boots anyone else
// signed in as you.
export default function ChangePasswordModal({ onClose, onNotify }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    if (next === current) {
      setError("Choose a password different from your current one.");
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      onNotify?.({ message: "Password changed. Other browsers signed in as you were signed out." });
      onClose();
    } catch (err) {
      setError(err.message || "Could not change your password.");
    } finally {
      setBusy(false);
    }
  }

  const field = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4"
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Change password</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            At least 8 characters. You'll stay signed in here; any other browser signed in as you won't.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
          <input
            type={show ? "text" : "password"}
            value={current}
            onChange={e => { setCurrent(e.target.value); setError(null); }}
            required
            autoComplete="current-password"
            className={field}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
          <input
            type={show ? "text" : "password"}
            value={next}
            onChange={e => { setNext(e.target.value); setError(null); }}
            required
            autoComplete="new-password"
            className={field}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
          <input
            type={show ? "text" : "password"}
            value={confirm}
            onChange={e => { setConfirm(e.target.value); setError(null); }}
            required
            autoComplete="new-password"
            className={field}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-500">
          <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} />
          Show passwords
        </label>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            disabled={busy}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2 rounded-lg text-sm transition-colors"
          >
            {busy ? "Changing…" : "Change password"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
